import type { BlockRole, ContentBlock, ContentFormat, DraftStatus, TextLimits } from '@app/shared';
import type { Dictionary } from '../i18n';

/*
 * The fields of «Формат и текст»: edited in place and saved by the autosave; the
 * API decides the saved version. The preview is only for the live form check.
 */

export interface EditorBlock {
  key: string;
  title: string;
  text: string;
}

export interface EditorState {
  blocks: EditorBlock[];
  dirty: boolean;
}

export type EditorAction =
  | { type: 'load'; blocks: ContentBlock[] }
  | { type: 'text'; key: string; value: string }
  | { type: 'title'; key: string; value: string }
  | { type: 'remove'; key: string }
  | { type: 'insertAfter'; key: string };

export const emptyEditor: EditorState = { blocks: [], dirty: false };

/** A key no block of `blocks` has: k1, k2, … */
function freshKey(blocks: EditorBlock[]): string {
  const used = blocks.map((b) => Number(b.key.slice(1))).filter(Number.isFinite);
  return `k${Math.max(0, ...used) + 1}`;
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'load':
      return {
        blocks: action.blocks.map((b, i) => ({ key: `k${i + 1}`, title: b.title ?? '', text: b.text })),
        dirty: false,
      };
    case 'text':
    case 'title': {
      const field = action.type;
      return {
        blocks: state.blocks.map((b) => (b.key === action.key ? { ...b, [field]: action.value } : b)),
        dirty: true,
      };
    }
    case 'remove': {
      const rest = state.blocks.filter((b) => b.key !== action.key);
      return {
        blocks: rest.length ? rest : [{ key: freshKey(state.blocks), title: '', text: '' }],
        dirty: true,
      };
    }
    case 'insertAfter': {
      const at = state.blocks.findIndex((b) => b.key === action.key);
      const blocks = [...state.blocks];
      blocks.splice(at + 1, 0, { key: freshKey(state.blocks), title: '', text: '' });
      return { blocks, dirty: true };
    }
  }
}

/** The body of PUT /drafts/:id/blocks. */
export function toBlocksInput(state: EditorState): Array<{ title?: string; text: string }> {
  return state.blocks.map((b) => (b.title.trim() ? { title: b.title, text: b.text } : { text: b.text }));
}

export function roleAt(index: number, total: number): BlockRole {
  return index === 0 ? 'hook' : index === total - 1 ? 'cta' : 'body';
}

/**
 * The fields as the API would store them, for the live `checkDraftForm`: trimmed,
 * roles by position, ids b1…, titles only on a carousel. Unlike the API, an
 * empty field stays in place so the check names it.
 */
export function previewBlocks(state: EditorState, format: ContentFormat): ContentBlock[] {
  const total = state.blocks.length;
  return state.blocks.map((b, i) => {
    const title = format === 'carousel' ? b.title.trim() : '';
    const block: ContentBlock = { id: `b${i + 1}`, role: roleAt(i, total), text: b.text.trim() };
    return title ? { ...block, title } : block;
  });
}

/** The carousel limits of a carousel draft; a draft of a type without one is a bug. */
function slideLimits(limits: TextLimits): NonNullable<TextLimits['carousel']> {
  if (!limits.carousel) throw new Error('this type has no carousel');
  return limits.carousel;
}

/** The character limit of a block's text by its format and role. */
export function textLimit(format: ContentFormat, role: BlockRole, limits: TextLimits): number {
  return format === 'reel' ? limits.reel.maxChars[role] : slideLimits(limits).roles[role].textMaxChars;
}

/** The character limit of a slide title by the slide's role. */
export function titleLimit(role: BlockRole, limits: TextLimits): number {
  return slideLimits(limits).roles[role].titleMaxChars;
}

/** «Крючок», «Блок 2», «Призыв»; «Слайд 1 · крючок», «Слайд 2», «Слайд 5 · призыв». */
export function blockLabel(format: ContentFormat, index: number, total: number, t: Dictionary): string {
  const role = roleAt(index, total);
  const n = index + 1;
  if (format === 'reel') return role === 'hook' ? t.draft.hook : role === 'cta' ? t.draft.cta : t.draft.block(n);
  return role === 'hook' ? t.draft.slideHook(n) : role === 'cta' ? t.draft.slideCta(n) : t.draft.slide(n);
}

/**
 * The last AI edit failed: the API puts the draft back (ready or approved) with
 * the error and without a new version. Read from the draft, so a person who left
 * during the edit is told too.
 */
export function lastEditFailed(draft: { status: DraftStatus; error: string | null }): boolean {
  return (draft.status === 'ready' || draft.status === 'approved') && draft.error !== null;
}

/** The same normalization as the API's flags: lower case, ё → е, spaces collapsed. */
const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();

/**
 * Which field each note of the check sits under (spec 4.4), by field key: the
 * field that holds its quote now; else the field at its block's place in the
 * saved version; else the last field. Fields move while the person edits, the
 * saved version's block ids do not.
 */
export function flagsByField<F extends { blockId: string; quote: string }>(
  flags: readonly F[],
  state: EditorState,
  saved: ReadonlyArray<{ id: string }>,
): Map<string, F[]> {
  const out = new Map<string, F[]>();
  const fields = state.blocks;
  if (fields.length === 0) return out;
  for (const f of flags) {
    const q = norm(f.quote);
    const holder = fields.find((b) => norm(`${b.title}\n${b.text}`).includes(q));
    const at = saved.findIndex((b) => b.id === f.blockId);
    const field = holder ?? fields[Math.min(at < 0 ? fields.length - 1 : at, fields.length - 1)]!;
    out.set(field.key, [...(out.get(field.key) ?? []), f]);
  }
  return out;
}
